import L from 'leaflet';
import React, { useEffect, useState, useMemo } from 'react';
import { FaTimes } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';

import { trackPageView } from '../../services/analytics';
import { UnifiedLoadingSpinner } from '../ui';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue - only in browser environment
if (typeof window !== 'undefined' && L.Icon && L.Icon.Default) {
  delete (L.Icon.Default.prototype as any)._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  });
}

interface MapModalProps {
  locations: string[];
  coordinates?: Array<{ name: string; lat: number; lng: number }>;
  isOpen: boolean;
  onClose: () => void;
}

interface LocationData {
  name: string;
  lat: number;
  lng: number;
  countryCode?: string;
}

// Major countries and regions coordinate data
// Create location data with both Japanese and English keys
const createLocationData = () => {
  const baseData = [
    {
      jp: 'モンゴル',
      en: 'Mongolia',
      name: 'モンゴル',
      lat: 46.8625,
      lng: 103.8467,
      countryCode: 'MN',
    },
    {
      jp: 'アルゼンチン',
      en: 'Argentina',
      name: 'アルゼンチン',
      lat: -34.6037,
      lng: -58.3816,
      countryCode: 'AR',
    },
    { jp: '中国', en: 'China', name: '中国', lat: 35.8617, lng: 104.1954, countryCode: 'CN' },
    {
      jp: 'アメリカ',
      en: 'America',
      name: 'アメリカ',
      lat: 39.8283,
      lng: -98.5795,
      countryCode: 'US',
    },
    { jp: 'カナダ', en: 'Canada', name: 'カナダ', lat: 56.1304, lng: -106.3468, countryCode: 'CA' },
    {
      jp: 'ブラジル',
      en: 'Brazil',
      name: 'ブラジル',
      lat: -14.235,
      lng: -51.9253,
      countryCode: 'BR',
    },
    {
      jp: 'オーストラリア',
      en: 'Australia',
      name: 'オーストラリア',
      lat: -25.2744,
      lng: 133.7751,
      countryCode: 'AU',
    },
    { jp: 'インド', en: 'India', name: 'インド', lat: 20.5937, lng: 78.9629, countryCode: 'IN' },
    { jp: 'ロシア', en: 'Russia', name: 'ロシア', lat: 61.524, lng: 105.3188, countryCode: 'RU' },
    { jp: '日本', en: 'Japan', name: '日本', lat: 36.2048, lng: 138.2529, countryCode: 'JP' },
    {
      jp: 'イギリス',
      en: 'Britain',
      name: 'イギリス',
      lat: 55.3781,
      lng: -3.436,
      countryCode: 'GB',
    },
    {
      jp: 'フランス',
      en: 'France',
      name: 'フランス',
      lat: 46.6034,
      lng: 1.8883,
      countryCode: 'FR',
    },
    { jp: 'ドイツ', en: 'Germany', name: 'ドイツ', lat: 51.1657, lng: 10.4515, countryCode: 'DE' },
    {
      jp: 'イタリア',
      en: 'Italy',
      name: 'イタリア',
      lat: 41.8719,
      lng: 12.5674,
      countryCode: 'IT',
    },
    {
      jp: 'スペイン',
      en: 'Spain',
      name: 'スペイン',
      lat: 40.4637,
      lng: -3.7492,
      countryCode: 'ES',
    },
    {
      jp: 'エジプト',
      en: 'Egypt',
      name: 'エジプト',
      lat: 26.0975,
      lng: 31.2357,
      countryCode: 'EG',
    },
    {
      jp: '南アフリカ',
      en: 'South Africa',
      name: '南アフリカ',
      lat: -30.5595,
      lng: 22.9375,
      countryCode: 'ZA',
    },
    { jp: 'ケニア', en: 'Kenya', name: 'ケニア', lat: -0.0236, lng: 37.9062, countryCode: 'KE' },
    {
      jp: 'タンザニア',
      en: 'Tanzania',
      name: 'タンザニア',
      lat: -6.369,
      lng: 34.8888,
      countryCode: 'TZ',
    },
    {
      jp: 'マダガスカル',
      en: 'Madagascar',
      name: 'マダガスカル',
      lat: -18.7669,
      lng: 46.8691,
      countryCode: 'MG',
    },
    { jp: 'チリ', en: 'Chile', name: 'チリ', lat: -35.6751, lng: -71.543, countryCode: 'CL' },
    { jp: 'ペルー', en: 'Peru', name: 'ペルー', lat: -9.19, lng: -75.0152, countryCode: 'PE' },
    {
      jp: 'メキシコ',
      en: 'Mexico',
      name: 'メキシコ',
      lat: 23.6345,
      lng: -102.5528,
      countryCode: 'MX',
    },
    { jp: 'イラン', en: 'Iran', name: 'イラン', lat: 32.4279, lng: 53.688, countryCode: 'IR' },
    { jp: 'イラク', en: 'Iraq', name: 'イラク', lat: 33.2232, lng: 43.6793, countryCode: 'IQ' },
    { jp: 'トルコ', en: 'Turkey', name: 'トルコ', lat: 38.9637, lng: 35.2433, countryCode: 'TR' },
    {
      jp: 'パキスタン',
      en: 'Pakistan',
      name: 'パキスタン',
      lat: 30.3753,
      lng: 69.3451,
      countryCode: 'PK',
    },
    {
      jp: 'アフガニスタン',
      en: 'Afghanistan',
      name: 'アフガニスタン',
      lat: 33.9391,
      lng: 67.71,
      countryCode: 'AF',
    },
  ];

  const result: { [key: string]: LocationData } = {};
  baseData.forEach(item => {
    const locationData = {
      name: item.name,
      lat: item.lat,
      lng: item.lng,
      countryCode: item.countryCode,
    };
    result[item.jp] = locationData; // Japanese key for backward compatibility
    result[item.en] = locationData; // English key to avoid non-ASCII warnings
  });
  return result;
};

const LOCATION_COORDINATES = createLocationData();

const MapModal: React.FC<MapModalProps> = ({ locations, coordinates, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      trackPageView('/modal/map', 'Map');
    }
  }, [isOpen]);
  const [loading, setLoading] = useState(false);

  // 位置データを取得（useMemoでメモ化）
  const locationData = useMemo(() => {
    // 座標データが提供されている場合はそれを使用
    if (coordinates && coordinates.length > 0) {
      return coordinates.map(coord => {
        // 座標から国コードを推定
        let countryCode: string | undefined;

        // 日本の座標範囲をチェック
        if (coord.lat >= 24.0 && coord.lat <= 46.0 && coord.lng >= 129.0 && coord.lng <= 146.0) {
          countryCode = 'JP';
        }
        // アメリカの座標範囲をチェック
        else if (
          coord.lat >= 24.0 &&
          coord.lat <= 71.0 &&
          coord.lng >= -180.0 &&
          coord.lng <= -66.0
        ) {
          countryCode = 'US';
        }
        // その他の国も必要に応じて追加

        return {
          name: coord.name,
          lat: coord.lat,
          lng: coord.lng,
          countryCode,
        };
      });
    }

    // フォールバック: 既存のロケーション座標データを使用
    return locations
      .map(location => LOCATION_COORDINATES[location])
      .filter(Boolean) as LocationData[];
  }, [locations, coordinates]);

  // ローディング状態を管理
  useEffect(() => {
    if (!isOpen || locationData.length === 0) return;

    // 座標データが提供されている場合は即座にローディング完了
    if (coordinates && coordinates.length > 0) {
      setLoading(false);
    } else {
      // フォールバックの場合も即座にローディング完了
      setLoading(false);
    }
  }, [isOpen, locationData, coordinates]);

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // モーダルが開いているときはスクロールを無効化
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // 座標データが見つからない場合のデフォルト表示
  if (locationData.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* オーバーレイ */}
        <div
          className="absolute inset-0 bg-black bg-opacity-80"
          onClick={onClose}
          onKeyDown={e => e.key === 'Escape' && onClose()}
          role="button"
          tabIndex={0}
          aria-label="モーダルを閉じる"
        />

        {/* モーダルコンテンツ */}
        <div className="relative z-10 w-full max-w-md mx-4 stone-texture-bg rounded-lg p-6">
          {/* ヘッダー */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold flex items-center">
              <span className="mr-2">🗺️</span>
              関連地域
            </h3>
            <button
              onClick={onClose}
              aria-label="モーダルを閉じる"
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="close-button"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* メッセージ */}
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">
              申し訳ございません。以下の地域の地図データが見つかりませんでした：
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {locations.map((location, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium"
                >
                  📍 {location}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 地図の中心とズームレベルを計算
  const bounds =
    locationData.length > 0
      ? locationData.reduce(
          (acc, location) => ({
            minLat: Math.min(acc.minLat, location.lat),
            maxLat: Math.max(acc.maxLat, location.lat),
            minLng: Math.min(acc.minLng, location.lng),
            maxLng: Math.max(acc.maxLng, location.lng),
          }),
          {
            minLat: locationData[0].lat,
            maxLat: locationData[0].lat,
            minLng: locationData[0].lng,
            maxLng: locationData[0].lng,
          }
        )
      : { minLat: 0, maxLat: 0, minLng: 0, maxLng: 0 };

  const center: [number, number] =
    locationData.length > 0
      ? [(bounds.minLat + bounds.maxLat) / 2, (bounds.minLng + bounds.maxLng) / 2]
      : [0, 0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* オーバーレイ */}
      <div
        className="absolute inset-0 bg-black bg-opacity-80"
        onClick={onClose}
        onKeyDown={e => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="モーダルを閉じる"
        data-testid="modal-overlay"
      />

      {/* モーダルコンテンツ */}
      <div className="relative z-10 w-full max-w-4xl h-full max-h-[80vh] mx-4 stone-texture-bg rounded-lg overflow-hidden">
        {/* ヘッダー */}
        <div className="flex justify-between items-center p-4 bg-green-600 text-white">
          <h3 className="text-lg font-bold flex items-center">
            <span className="mr-2">🗺️</span>
            関連地域マップ
          </h3>
          <button
            onClick={onClose}
            aria-label="モーダルを閉じる"
            className="p-2 hover:bg-green-700 rounded-full transition-colors"
            data-testid="close-button"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* 地図エリア */}
        <div className="h-full">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <UnifiedLoadingSpinner size="xl" color="green" variant="spinner" />
              <span className="ml-3 text-gray-600">地図を読み込み中...</span>
            </div>
          ) : (
            <MapContainer
              center={center}
              zoom={locationData.length === 1 ? 8 : 6}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={18}
                minZoom={1}
                tileSize={256}
                zoomOffset={0}
              />

              {/* マーカー */}
              {locationData.map((location, index) => (
                <Marker key={index} position={[location.lat, location.lng]}>
                  <Popup>
                    <div className="text-center">
                      <h4 className="font-bold text-lg">{location.name}</h4>
                      <p className="text-sm text-gray-600">古生物学的発見地域</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* フッター */}
        <div className="p-4 bg-gray-50 border-t">
          <div className="flex flex-wrap gap-2">
            {locationData.map((location, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
              >
                📍 {location.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapModal;
